import {
  DocumentType,
  SubmissionDocumentType,
  DocumentStatus,
} from "generated-graphql/graphql";

export interface SubmissionDocumentMetadata {
  id: string;
  name: string;
  documentType: DocumentType;
  submissionDocumentType: SubmissionDocumentType;
  status?: DocumentStatus;
  url?: string;
  fileExtension?: string | null;
}


interface PartitionedDocuments {
  submissionDocuments: SubmissionDocumentMetadata[];
  attachmentDocuments: SubmissionDocumentMetadata[];
}

/**
 * Separates submission documents from attachments based on document type
 */
export function partitionSubmissionDocuments(
  documents: SubmissionDocumentMetadata[]
): PartitionedDocuments {
  const submissionDocuments: SubmissionDocumentMetadata[] = [];
  const attachmentDocuments: SubmissionDocumentMetadata[] = [];

  documents.forEach((doc) => {
    if (
      doc.submissionDocumentType === SubmissionDocumentType.Submission ||
      doc.submissionDocumentType ===
        SubmissionDocumentType.RejectedSubmission ||
      doc.submissionDocumentType === SubmissionDocumentType.SignedSubmission
    ) {
      submissionDocuments.push(doc);
    } else if (
      doc.submissionDocumentType === SubmissionDocumentType.Attachment
    ) {
      attachmentDocuments.push(doc);
    }
  });

  return { submissionDocuments, attachmentDocuments };
}

/**
 * Sorts submission documents by type priority:
 * 1. Signed submissions (first)
 * 2. Regular submissions
 * 3. Rejected submissions (last)
 * 4. Drafts (should be at top but currently go to bottom - known issue)
 */
export function sortSubmissionDocumentsByType(
  documents: SubmissionDocumentMetadata[]
): SubmissionDocumentMetadata[] {
  return [...documents].sort((a, b) => {
    const aIsSigned =
      a.submissionDocumentType === SubmissionDocumentType.SignedSubmission;
    const bIsSigned =
      b.submissionDocumentType === SubmissionDocumentType.SignedSubmission;
    const aIsRejected =
      a.submissionDocumentType === SubmissionDocumentType.RejectedSubmission;
    const bIsRejected =
      b.submissionDocumentType === SubmissionDocumentType.RejectedSubmission;

    // Signed submissions first
    if (aIsSigned && !bIsSigned) {
      return -1;
    }
    if (!aIsSigned && bIsSigned) {
      return 1;
    }

    // Rejected submissions last
    if (aIsRejected && !bIsRejected) {
      return 1;
    }
    if (!aIsRejected && bIsRejected) {
      return -1;
    }

    return 0;
  });
}

interface Reviewer {
  id: string;
  isSigner?: boolean;
  [key: string]: any;
}

interface PartitionedReviewers<T extends Reviewer> {
  signer: T | undefined;
  reviewers: T[];
}

/**
 * Extracts the signer from the list of reviewers and returns both separately
 * Note: Currently assumes only one signer
 */
export function partitionReviewersAndSigners<T extends Reviewer>(
  reviewers: T[]
): PartitionedReviewers<T> {
  const signer = reviewers.find((r) => r.isSigner);
  const nonSignerReviewers = reviewers.filter((r) => !r.isSigner);

  return {
    signer,
    reviewers: nonSignerReviewers,
  };
}
